import { useState, useEffect } from 'react';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';

interface TimelineTask {
  id: string;
  title: string;
  description: string;
  timeframe: string;
  category: 'visa' | 'financial' | 'housing' | 'logistics' | 'personal';
  completed: boolean;
  priority: 'high' | 'medium' | 'low';
}

export default function TimelinePlannerPage() {
  const currentYear = new Date().getFullYear();
  const [moveDate, setMoveDate] = useState('');
  const [visaType, setVisaType] = useState<'work' | 'student' | 'critical-skills' | 'general'>('work');
  
  useEffect(() => {
    document.title = 'Ireland Move Timeline Planner - Step-by-Step Relocation Schedule | Ireland Bound';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Interactive timeline planner for moving to Ireland. Track tasks, deadlines, and milestones from 12 months before to your first month in Ireland.');
    }
  }, []);
  
  const allTasks: TimelineTask[] = [
    // 12+ months before
    {
      id: '1',
      title: 'Research visa requirements',
      description: 'Understand which visa type you need and gather required documents',
      timeframe: '12+ months',
      category: 'visa',
      completed: false,
      priority: 'high'
    },
    {
      id: '2',
      title: 'Start saving money',
      description: 'Build emergency fund of €10,000-15,000 for initial expenses',
      timeframe: '12+ months',
      category: 'financial',
      completed: false,
      priority: 'high'
    },
    {
      id: '3',
      title: 'Research job market',
      description: 'Identify in-demand skills and potential employers in Ireland',
      timeframe: '12+ months',
      category: 'personal',
      completed: false,
      priority: 'medium'
    },
    
    // 9-12 months before
    {
      id: '4',
      title: 'Secure job offer (if applicable)',
      description: 'Apply for jobs and obtain employment contract for work visa',
      timeframe: '9-12 months',
      category: 'visa',
      completed: false,
      priority: 'high'
    },
    {
      id: '5',
      title: 'Apply for visa',
      description: 'Submit complete visa application with all supporting documents',
      timeframe: '9-12 months',
      category: 'visa',
      completed: false,
      priority: 'high'
    },
    {
      id: '6',
      title: 'Get passport renewed',
      description: 'Ensure passport is valid for at least 6 months after move date',
      timeframe: '9-12 months',
      category: 'logistics',
      completed: false,
      priority: 'high'
    },
    
    // 6-9 months before
    {
      id: '7',
      title: 'Research housing options',
      description: 'Explore neighborhoods and rental prices in your target city',
      timeframe: '6-9 months',
      category: 'housing',
      completed: false,
      priority: 'medium'
    },
    {
      id: '8',
      title: 'Organize financial documents',
      description: 'Gather bank statements, tax returns, employment records',
      timeframe: '6-9 months',
      category: 'financial',
      completed: false,
      priority: 'medium'
    },
    {
      id: '9',
      title: 'Plan pet relocation (if applicable)',
      description: 'Start microchipping, vaccinations, and health certificate process',
      timeframe: '6-9 months',
      category: 'logistics',
      completed: false,
      priority: 'high'
    },
    
    // 3-6 months before
    {
      id: '10',
      title: 'Book flights',
      description: 'Reserve one-way tickets to Ireland',
      timeframe: '3-6 months',
      category: 'logistics',
      completed: false,
      priority: 'high'
    },
    {
      id: '11',
      title: 'Arrange temporary accommodation',
      description: 'Book Airbnb or hotel for first 2-4 weeks',
      timeframe: '3-6 months',
      category: 'housing',
      completed: false,
      priority: 'high'
    },
    {
      id: '12',
      title: 'Notify current employer',
      description: 'Give proper notice and arrange final paycheck',
      timeframe: '3-6 months',
      category: 'personal',
      completed: false,
      priority: 'high'
    },
    {
      id: '13',
      title: 'Research Irish tax obligations',
      description: 'Understand dual tax filing requirements for US citizens',
      timeframe: '3-6 months',
      category: 'financial',
      completed: false,
      priority: 'medium'
    },
    
    // 1-3 months before
    {
      id: '14',
      title: 'Sell or store belongings',
      description: 'Decide what to ship, sell, or put in storage',
      timeframe: '1-3 months',
      category: 'logistics',
      completed: false,
      priority: 'medium'
    },
    {
      id: '15',
      title: 'Cancel US subscriptions',
      description: 'End gym memberships, streaming services, utilities',
      timeframe: '1-3 months',
      category: 'logistics',
      completed: false,
      priority: 'low'
    },
    {
      id: '16',
      title: 'Get medical checkup',
      description: 'Complete any needed healthcare before leaving US',
      timeframe: '1-3 months',
      category: 'personal',
      completed: false,
      priority: 'medium'
    },
    {
      id: '17',
      title: 'Notify banks and credit cards',
      description: 'Inform financial institutions of international move',
      timeframe: '1-3 months',
      category: 'financial',
      completed: false,
      priority: 'high'
    },
    
    // 2-4 weeks before
    {
      id: '18',
      title: 'Pack essentials',
      description: 'Pack 2 suitcases with immediate necessities',
      timeframe: '2-4 weeks',
      category: 'logistics',
      completed: false,
      priority: 'high'
    },
    {
      id: '19',
      title: 'Set up mail forwarding',
      description: 'Arrange USPS mail forwarding to family/friend',
      timeframe: '2-4 weeks',
      category: 'logistics',
      completed: false,
      priority: 'medium'
    },
    {
      id: '20',
      title: 'Download important documents',
      description: 'Save digital copies of all important papers',
      timeframe: '2-4 weeks',
      category: 'logistics',
      completed: false,
      priority: 'high'
    },
    
    // First week in Ireland
    {
      id: '21',
      title: 'Register with immigration',
      description: 'Visit GNIB/INIS office to register your arrival',
      timeframe: 'Week 1',
      category: 'visa',
      completed: false,
      priority: 'high'
    },
    {
      id: '22',
      title: 'Apply for PPS number',
      description: 'Get Personal Public Service number (like SSN)',
      timeframe: 'Week 1',
      category: 'financial',
      completed: false,
      priority: 'high'
    },
    {
      id: '23',
      title: 'Open Irish bank account',
      description: 'Set up local banking with proof of address',
      timeframe: 'Week 1',
      category: 'financial',
      completed: false,
      priority: 'high'
    },
    {
      id: '24',
      title: 'Get Irish phone number',
      description: 'Purchase SIM card or phone plan',
      timeframe: 'Week 1',
      category: 'logistics',
      completed: false,
      priority: 'high'
    },
    
    // First month
    {
      id: '25',
      title: 'Find permanent housing',
      description: 'Secure long-term rental and sign lease',
      timeframe: 'Month 1',
      category: 'housing',
      completed: false,
      priority: 'high'
    },
    {
      id: '26',
      title: 'Register with GP',
      description: 'Find and register with local doctor',
      timeframe: 'Month 1',
      category: 'personal',
      completed: false,
      priority: 'medium'
    },
    {
      id: '27',
      title: 'Set up utilities',
      description: 'Arrange electricity, gas, internet for new home',
      timeframe: 'Month 1',
      category: 'housing',
      completed: false,
      priority: 'high'
    },
    {
      id: '28',
      title: 'Apply for Irish driving license',
      description: 'Exchange US license or apply for Irish license',
      timeframe: 'Month 1',
      category: 'logistics',
      completed: false,
      priority: 'low'
    }
  ];

  const [tasks, setTasks] = useState(allTasks);

  const toggleTask = (id: string) => {
    setTasks(tasks.map(task => 
      task.id === id ? { ...task, completed: !task.completed } : task
    ));
  };

  const categoryColors = {
    visa: 'bg-purple-100 text-purple-700',
    financial: 'bg-emerald-100 text-emerald-700',
    housing: 'bg-blue-100 text-blue-700',
    logistics: 'bg-orange-100 text-orange-700',
    personal: 'bg-pink-100 text-pink-700'
  };

  const categoryIcons = {
    visa: 'ri-passport-line',
    financial: 'ri-money-dollar-circle-line',
    housing: 'ri-home-4-line',
    logistics: 'ri-truck-line',
    personal: 'ri-user-line'
  };

  const timeframes = ['12+ months', '9-12 months', '6-9 months', '3-6 months', '1-3 months', '2-4 weeks', 'Week 1', 'Month 1'];

  const completedCount = tasks.filter(t => t.completed).length;
  const progressPercent = Math.round((completedCount / tasks.length) * 100);

  return (
    <>
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
        <Navbar />

        <div className="pt-24 pb-16 px-4">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <i className="ri-calendar-check-line"></i>
                Interactive Timeline Tool
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-4">
                Ireland Move Timeline Planner
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Your personalized step-by-step timeline for moving to Ireland. Track every task from 12 months before to your first month in Ireland.
              </p>
            </div>

            {/* Settings */}
            <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Move Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Planned Move Date
                  </label>
                  <input
                    type="date"
                    value={moveDate}
                    onChange={(e) => setMoveDate(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Visa Type
                  </label>
                  <select
                    value={visaType}
                    onChange={(e) => setVisaType(e.target.value as any)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  >
                    <option value="work">Work Permit</option>
                    <option value="critical-skills">Critical Skills Employment Permit</option>
                    <option value="student">Student Visa</option>
                    <option value="general">General Employment Permit</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Progress */}
            <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-xl p-8 text-white mb-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold mb-2">Your Progress</h2>
                  <p className="text-emerald-100">
                    {completedCount} of {tasks.length} tasks completed
                  </p>
                </div>
                <div className="text-5xl font-bold">
                  {progressPercent}%
                </div>
              </div>
              <div className="w-full bg-emerald-800 rounded-full h-4">
                <div 
                  className="bg-white rounded-full h-4 transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                ></div>
              </div>
            </div>

            {/* Timeline */}
            {timeframes.map((timeframe) => {
              const timeframeTasks = tasks.filter(t => t.timeframe === timeframe);
              if (timeframeTasks.length === 0) return null;

              return (
                <div key={timeframe} className="mb-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold">
                      <i className="ri-time-line text-xl"></i>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">{timeframe} Before Move</h2>
                  </div>

                  <div className="space-y-4 ml-6 border-l-4 border-emerald-200 pl-8">
                    {timeframeTasks.map((task) => (
                      <div
                        key={task.id}
                        className={`bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all cursor-pointer ${
                          task.completed ? 'opacity-60' : ''
                        }`}
                        onClick={() => toggleTask(task.id)}
                      >
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0 mt-1">
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                              task.completed 
                                ? 'bg-emerald-600 border-emerald-600' 
                                : 'border-gray-300'
                            }`}>
                              {task.completed && (
                                <i className="ri-check-line text-white text-sm"></i>
                              )}
                            </div>
                          </div>

                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-2">
                              <h3 className={`text-lg font-bold ${
                                task.completed ? 'line-through text-gray-500' : 'text-gray-900'
                              }`}>
                                {task.title}
                              </h3>
                              <div className="flex items-center gap-2">
                                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${categoryColors[task.category]}`}>
                                  <i className={`${categoryIcons[task.category]} mr-1`}></i>
                                  {task.category}
                                </span>
                                {task.priority === 'high' && (
                                  <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                                    High Priority
                                  </span>
                                )}
                              </div>
                            </div>
                            <p className="text-gray-600">{task.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* CTA */}
            <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-xl p-8 text-white text-center">
              <h2 className="text-3xl font-bold mb-4">Need Help Planning Your Move?</h2>
              <p className="text-xl text-emerald-50 mb-6">
                Chat with our Relocation Expert for personalized guidance on your timeline
              </p>
              <button
                onClick={() => {
                  const widget = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
                  if (widget) widget.click();
                }}
                className="bg-white text-emerald-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-emerald-50 transition-colors whitespace-nowrap inline-flex items-center gap-2"
              >
                <i className="ri-chat-3-line text-xl"></i>
                Talk to Relocation Expert
              </button>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}
